/**
 * An enum to define\list all the available logging events in product.
 * Depending on the event, framework will decide where [in which table] to log the given event.
 * This can be helpful during issue investigation because all the event usage logs will go in a separate table.
 *
 *  @see ITelemetryLogger.logData
 */
export enum LogEventNames {
  // Maintain the alphabetical order, while adding new events.
  // Make sure to also add an entry into LogEvents.ts
  Augloop = 'Augloop',
  AugloopRealTimeTranscriptionConnection = 'AugloopRealTimeTranscriptionConnection',
  AuthPopup = 'AuthPopup',
  Boot = 'Boot',
  DocumentStorage = 'DocumentStorage',
  Error = 'Error',
  FileUpload = 'FileUpload',
  GetAccessTokenDiag = 'GetAccessTokenDiag',
  IndexeddbBackup = 'IndexeddbBackup',
  LiveRecording = 'LiveRecording',
  Microphone = 'Microphone',
  MonthlyQuota = 'MonthlyQuota',
  OfficeJS = 'OfficeJS',
  RenderToPane = 'RenderToPane',
  Recovery = 'Recovery',
  ServiceAuthentication = 'ServiceAuthentication',
  ServiceBatchTranscription = 'ServiceBatchTranscription',
  ServiceBatchTranscriptionInitialize = 'ServiceBatchTranscriptionInitialize',
  ServiceBatchTranscriptionGetResult = 'ServiceBatchTranscriptionGetResult',
  ServiceFrontdoorUpload = 'ServiceFrontdoorUpload',
  ServiceGetAccessToken = 'ServiceGetAccessToken',
  ServiceGraphClientOneDriveItem = 'ServiceGraphClientOneDriveItem',
  ServiceGraphClientOneDriveUpload = 'ServiceGraphClientOneDriveUpload',
  ServiceGraphOneDriveItem = 'ServiceGraphOneDriveItem',
  ServiceGraphOneDriveUpload = 'ServiceGraphOneDriveUpload',
  ServiceLiveSpeechToText = 'ServiceLiveSpeechToText',
  StartOver = 'StartOver',
  StoredTranscriptionIndicator = 'StoredTranscriptionIndicator',
  Transcript = 'Transcript',
  Upsell = 'Upsell',
  Warning = 'Warning',
  WavFormat = 'WavFormat',
  // OneNote Specific Log Events
  LoadLinkedOneNotePageInfo = 'LoadLinkedOneNotePageInfo',
  NavigateToLinkedOneNotePage = 'NavigateToLinkedOneNotePage',
  OneNoteUserEvents = 'OneNoteUserEvents',
  NetworkConnectivity = 'NetworkConnectivity',
  ServiceCopilotLicensingInitialize = 'ServiceCopilotLicensingInitialize'
}

export type LogValueType = number | string | boolean;

export interface ILogData<T extends string> {
    key: T,
    value: LogValueType,
};

export interface ITelemetryEvent<T extends string> {
  eventName: LogEventNames,
  data: ILogData<T>[]
};

export interface ITelemetryError{
  area: LogEventNames,
  message: string
};

/**
 * A generic logger interface which will be used as an handle to log telemetry events and errors.
 * Different implementations of ITelemetryLogger can co-exists to provide either different technology\library for logging and\or different configurations of logging frameworks.
 */
export interface ITelemetryLogger {
  LogEvent<T extends string>(event: ITelemetryEvent<T>): void;
  LogError(error: ITelemetryError): void
}
